import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sidebar,
  SidebarRail,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
  useSidebar
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Users,
  Building2,
  Shield,
  BarChart3,
  Settings,
  Bell,
  Database,
  FileText,
  Mail,
  UserCog,
  Webhook,
  MessageSquare,
  Activity,
  ShieldCheck,
  AlertTriangle,
  Brain,
  Palette,
  User,
  LogOut,
  TestTube2,
  Globe,
  Check,
  Mic,
  MonitorSpeaker,
  DollarSign,
  FlaskConical,
  MessageSquareText,
  ServerCog,
  ClipboardList
} from "lucide-react";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';

interface NavigationItem {
  titleKey: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  descriptionKey?: string;
  isThemeToggle?: boolean;
}

interface NavigationSection {
  titleKey: string;
  items: NavigationItem[];
}

// title/description text lives in the platformAdmin/common translation
// namespaces (see platformAdmin:sidebar.*) — this array only carries the
// i18next keys, resolved with t() at render time.
const navigationSections: NavigationSection[] = [
  {
    titleKey: "common:main",
    items: [
      {
        titleKey: "platformAdmin:overview",
        href: "/platform-admin",
        icon: BarChart3,
        descriptionKey: "platformAdmin:sidebar.items.overview.description"
      },
      {
        titleKey: "platformAdmin:sidebar.items.platformUsers.title",
        href: "/platform-admin/users",
        icon: Users,
        descriptionKey: "platformAdmin:sidebar.items.platformUsers.description"
      }
    ]
  },
  {
    titleKey: "platformAdmin:sidebar.sections.tenantManagement",
    items: [
      {
        titleKey: "platformAdmin:sidebar.items.allTenants.title",
        href: "/platform-admin/spaces",
        icon: Building2,
        descriptionKey: "platformAdmin:sidebar.items.allTenants.description"
      },
      {
        titleKey: "platformAdmin:sidebar.items.formLinks.title",
        href: "/platform-admin/forms",
        icon: ClipboardList,
        descriptionKey: "platformAdmin:sidebar.items.formLinks.description"
      }
    ]
  },
  {
    titleKey: "platformAdmin:sidebar.sections.globalManagement",
    items: [
      {
        titleKey: "platformAdmin:sidebar.items.aiManagement.title",
        href: "/platform-admin/ai-management",
        icon: Brain,
        descriptionKey: "platformAdmin:sidebar.items.aiManagement.description"
      },
      {
        titleKey: "platformAdmin:sidebar.items.testModels.title",
        href: "/platform-admin/test-models",
        icon: FlaskConical,
        descriptionKey: "platformAdmin:sidebar.items.testModels.description"
      },
      {
        titleKey: "platformAdmin:topNav.globalManagement.aiTemplates",
        href: "/platform-admin/ai-templates",
        icon: Settings,
        descriptionKey: "platformAdmin:sidebar.items.aiTemplates.description"
      },
      {
        titleKey: "platformAdmin:topNav.globalManagement.systemLogs",
        href: "/platform-admin/logging",
        icon: FileText,
        descriptionKey: "platformAdmin:sidebar.items.systemLogs.description"
      },
      {
        titleKey: "platformAdmin:sidebar.items.apiHealth.title",
        href: "/platform-admin/api-health",
        icon: Activity,
        descriptionKey: "platformAdmin:sidebar.items.apiHealth.description"
      },
      {
        titleKey: "platformAdmin:sidebar.items.rehearsalEnvironment.title",
        href: "/platform-admin/rehearsal-environment",
        icon: ServerCog,
        descriptionKey: "platformAdmin:sidebar.items.rehearsalEnvironment.description"
      },
      {
        titleKey: "common:systemHealth",
        href: "/platform-admin/system-health",
        icon: MonitorSpeaker,
        descriptionKey: "platformAdmin:sidebar.items.systemHealth.description"
      },
      {
        titleKey: "platformAdmin:sidebar.items.webhookSecurity.title",
        href: "/platform-admin/webhook-security",
        icon: ShieldCheck,
        descriptionKey: "platformAdmin:sidebar.items.webhookSecurity.description"
      },
      {
        titleKey: "platformAdmin:topNav.globalManagement.billingOverview",
        href: "/platform-admin/billing",
        icon: DollarSign,
        descriptionKey: "platformAdmin:sidebar.items.billingOverview.description"
      },
      {
        titleKey: "platformAdmin:sections.templateLibrary.title",
        href: "/platform-admin/templates",
        icon: Database,
        descriptionKey: "platformAdmin:sidebar.items.templateLibrary.description"
      },
      {
        titleKey: "platformAdmin:topNav.globalManagement.conversationTesting",
        href: "/platform-admin/conversation-testing",
        icon: TestTube2,
        descriptionKey: "platformAdmin:sidebar.items.conversationTesting.description"
      },
      {
        titleKey: "platformAdmin:sidebar.items.conversationSimulator.title",
        href: "/platform-admin/conversation-simulator",
        icon: MessageSquareText,
        descriptionKey: "platformAdmin:sidebar.items.conversationSimulator.description"
      },
      {
        titleKey: "platformAdmin:sidebar.items.testingPlatform.title",
        href: "/platform-admin/testing",
        icon: AlertTriangle,
        descriptionKey: "platformAdmin:sidebar.items.testingPlatform.description"
      }
    ]
  },
  {
    titleKey: "platformAdmin:sidebar.sections.labsExperiments",
    items: [
      {
        titleKey: "platformAdmin:sidebar.items.voiceLabDemo.title",
        href: "/platform-admin/voice-lab",
        icon: Mic,
        descriptionKey: "platformAdmin:sidebar.items.voiceLabDemo.description"
      }
    ]
  },
  {
    titleKey: "common:channels",
    items: [
      {
        titleKey: "common:whatsapp",
        href: "/platform-admin/whatsapp",
        icon: MessageSquare,
        descriptionKey: "platformAdmin:sidebar.items.whatsapp.description"
      }
    ]
  },
  {
    titleKey: "common:preferences",
    items: [
      {
        titleKey: "common:theme",
        href: "#",
        icon: Palette,
        descriptionKey: "platformAdmin:sidebar.items.theme.description",
        isThemeToggle: true
      }
    ]
  }
];

export function PlatformAdminNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const { user, userProfile, signOut, isReadOnlySession } = usePlatformAdmin();
  const { t, i18n } = useTranslation(['platformAdmin', 'common']);
  const collapsed = state === "collapsed";

  const getUserInitials = (name?: string) => {
    if (!name) return "PA";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <Sidebar className="border-r" collapsible="icon">
      <SidebarRail />

      {/* Header */}
      <SidebarHeader className="border-b border-border">
        <div className="flex items-center gap-2 px-4 py-3 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:justify-center">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
            <h2 className="font-semibold text-foreground">OKOS</h2>
            <p className="text-xs text-muted-foreground">{t('topNav.title')}</p>
          </div>
          <SidebarTrigger />
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2">
        <TooltipProvider>
          <ScrollArea className="flex-1">
            <div className="space-y-2 py-2">
              {navigationSections.map((section) => (
                <SidebarGroup key={section.titleKey}>
                  {!collapsed && (
                    <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {t(section.titleKey)}
                    </SidebarGroupLabel>
                  )}
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.href;

                        // Special handling for theme toggle
                        if (item.isThemeToggle) {
                          // T-07: this row has no click handler of its own -- it only hosts
                          // the theme toggle's own trigger button. `SidebarMenuButton` without
                          // `asChild` renders a real `<button>`, and `<ThemeToggle>` renders
                          // another one inside it (its dropdown trigger), so the browser saw a
                          // button nested inside a button on every page this sidebar appears
                          // on. `asChild` makes the menu-button styling land on the plain `div`
                          // below instead of creating its own button, leaving the toggle's
                          // button as the only interactive element in this row.
                          const menuButton = (
                            <SidebarMenuButton
                              asChild
                              className={cn(
                                "h-9 cursor-default",
                                collapsed ? "justify-center" : "justify-start"
                              )}
                            >
                              <div className={cn(
                                "flex items-center w-full",
                                collapsed ? "justify-center" : "gap-3"
                              )}>
                                <Icon className="h-4 w-4 shrink-0" />
                                {!collapsed && (
                                  <div className="flex items-center justify-between w-full">
                                    <span className="truncate text-sm">{t(item.titleKey)}</span>
                                    <ThemeToggle />
                                  </div>
                                )}
                                {collapsed && <ThemeToggle />}
                              </div>
                            </SidebarMenuButton>
                          );

                          return (
                            <SidebarMenuItem key={item.href}>
                              {collapsed ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    {menuButton}
                                  </TooltipTrigger>
                                  <TooltipContent side="right" className="flex flex-col">
                                    <span className="font-medium">{t(item.titleKey)}</span>
                                    {item.descriptionKey && (
                                      <span className="text-xs text-muted-foreground">
                                        {t(item.descriptionKey)}
                                      </span>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                menuButton
                              )}
                            </SidebarMenuItem>
                          );
                        }

                        const menuButton = (
                          <SidebarMenuButton
                            asChild
                            isActive={isActive}
                            tooltip={t(item.titleKey)}
                          >
                            <Link to={item.href}>
                              <Icon className="h-4 w-4" />
                              <span>{t(item.titleKey)}</span>
                            </Link>
                          </SidebarMenuButton>
                        );

                        return (
                          <SidebarMenuItem key={item.href}>
                            {collapsed ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {menuButton}
                                </TooltipTrigger>
                                <TooltipContent side="right" className="flex flex-col">
                                  <span className="font-medium">{t(item.titleKey)}</span>
                                  {item.descriptionKey && (
                                    <span className="text-xs text-muted-foreground">
                                      {t(item.descriptionKey)}
                                    </span>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              menuButton
                            )}
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </div>
          </ScrollArea>
        </TooltipProvider>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="border-t border-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-accent data-[state=open]:text-accent-foreground"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.user_metadata?.avatar_url} alt={userProfile?.full_name || t('platformAdmin:sidebar.footer.userAltFallback')} />
                    <AvatarFallback>{getUserInitials(userProfile?.full_name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <span className="text-sm font-medium truncate w-full">
                      {userProfile?.full_name || t('common:platformAdmin')}
                    </span>
                    <span className="text-xs text-muted-foreground truncate w-full">
                      {user?.email}
                    </span>
                    {/* T-11: a scoped read-only console session says so where the
                        operator already looks for who they are, instead of letting
                        them discover it when an action fails. */}
                    {isReadOnlySession && (
                      <Badge variant="secondary" className="mt-1 text-[10px] font-normal">
                        {t('platformAdmin:session.readOnlyBadge')}
                      </Badge>
                    )}
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="end"
                className="w-[240px]"
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{userProfile?.full_name || t('common:platformAdmin')}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <Badge variant="default" className="w-fit text-xs mt-1">
                      {t('common:platformAdmin')}
                    </Badge>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/platform-admin/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  {t('common:profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/platform-admin/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t('common:settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Globe className="mr-2 h-4 w-4" />
                    {t('platformAdmin:sidebar.footer.language')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onSelect={() => setTimeout(() => i18n.changeLanguage('en'), 0)}>
                      {i18n.language === 'en' && <Check className="mr-2 h-4 w-4" />}
                      {i18n.language !== 'en' && <span className="mr-6" />}
                      🇺🇸 English
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setTimeout(() => i18n.changeLanguage('es-CL'), 0)}>
                      {i18n.language === 'es-CL' && <Check className="mr-2 h-4 w-4" />}
                      {i18n.language !== 'es-CL' && <span className="mr-6" />}
                      🇨🇱 Español (Chile)
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('common:signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}